/**
 * StatsRow — hero metric tiles for the inquiry pipeline.
 *
 * POL-16 redesign. The old `.metric-card` recipe (flat dark box +
 * orbit-orange active bar) is gone; the tiles now share DNA with
 * ThisWeekStrip and the shortcut row: ink-gradient surface
 * (`.orbit-box-dark`), a Pixieset-tone icon chip per stat, value
 * top-right, label below. The active filter lights the tile in its
 * OWN tone (border + value color) instead of orange-for-everything.
 *
 * Five filter cards: Total / Pending / Accepted / Follow up /
 * Client portals. Tapping a card filters the table below.
 */
import { StatsData, InquiryStatus } from '@/types/crm';
import { FileText, Clock, CheckCircle, AlertTriangle, Monitor } from 'lucide-react';

interface StatsRowProps {
  stats: StatsData;
  activeFilter?: InquiryStatus | 'all' | 'portals';
  onStatClick?: (filter: InquiryStatus | 'all' | 'portals') => void;
}

type StatTone = 'blue' | 'yellow' | 'green' | 'red' | 'teal';

// Same accent family as the Today shortcut tiles (Pixieset ref).
//
// BRAND-1 (Aug 2026) — was stock Tailwind sky/amber/emerald/red/teal;
// now the 1960s space-race accents from index.css. The architecture is
// untouched (same keys, same four fields, same consumers) — only the
// colour family moved. And the roles line up almost too neatly with
// what these five cards already meant: Total = Mission Blue (brand),
// Pending = Kapton Gold (waiting), Accepted = Phosphor Green (money
// in), Follow up = Range Red (something's wrong), Client portals =
// Instrument Teal (secondary surface). Colour is now a legend, not
// decoration — and still deliberately NOT orange-for-everything.
//
// Two tokens per accent, NOT interchangeable:
//   • `orbit-*`      — READABLE tone, theme-aware (flips itself on
//                      light vs dark), which is why the `dark:`
//                      variants that used to live here are gone.
//   • `orbit-*-fill` — CHIP FILL tone, same value in both themes.
// Icon/value text uses the readable tone; chip backgrounds and the
// active ring use fill/readable respectively so the lit tile reads
// as a border+value change, not a flood.
const TONES: Record<
  StatTone,
  { chip: string; icon: string; activeRing: string; activeValue: string }
> = {
  blue: {
    chip: 'bg-orbit-blue-fill/[0.22] border-orbit-blue-fill/[0.42]',
    icon: 'text-orbit-blue',
    activeRing: 'border-orbit-blue/70 ring-1 ring-orbit-blue/40',
    activeValue: 'text-orbit-blue',
  },
  yellow: {
    chip: 'bg-orbit-gold-fill/[0.22] border-orbit-gold-fill/[0.42]',
    icon: 'text-orbit-gold',
    activeRing: 'border-orbit-gold/70 ring-1 ring-orbit-gold/40',
    activeValue: 'text-orbit-gold',
  },
  green: {
    chip: 'bg-orbit-phosphor-fill/[0.22] border-orbit-phosphor-fill/[0.42]',
    icon: 'text-orbit-phosphor',
    activeRing: 'border-orbit-phosphor/70 ring-1 ring-orbit-phosphor/40',
    activeValue: 'text-orbit-phosphor',
  },
  red: {
    chip: 'bg-orbit-red-fill/[0.22] border-orbit-red-fill/[0.42]',
    icon: 'text-orbit-red',
    activeRing: 'border-orbit-red/70 ring-1 ring-orbit-red/40',
    activeValue: 'text-orbit-red',
  },
  teal: {
    chip: 'bg-orbit-teal-fill/[0.22] border-orbit-teal-fill/[0.42]',
    icon: 'text-orbit-teal',
    activeRing: 'border-orbit-teal/70 ring-1 ring-orbit-teal/40',
    activeValue: 'text-orbit-teal',
  },
};

// Mission Control discipline (ORBIT_DESIGN.md §3.3): sentence case
// for all human-readable text. Stat labels are statements, not titles.
const statCards = [
  { key: 'total',         label: 'Total inquiries', icon: FileText,      tone: 'blue' as StatTone,   filter: 'all' as const },
  { key: 'pending',       label: 'Pending',         icon: Clock,         tone: 'yellow' as StatTone, filter: 'pending' as InquiryStatus },
  { key: 'accepted',      label: 'Accepted',        icon: CheckCircle,   tone: 'green' as StatTone,  filter: 'accepted' as InquiryStatus },
  { key: 'followUp',      label: 'Follow up',       icon: AlertTriangle, tone: 'red' as StatTone,    filter: 'follow_up' as InquiryStatus },
  { key: 'activePortals', label: 'Client portals',  icon: Monitor,       tone: 'teal' as StatTone,   filter: 'portals' as const },
] as const;

export const StatsRow = ({ stats, activeFilter, onStatClick }: StatsRowProps) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 stagger-children">
      {statCards.map((card) => {
        const Icon = card.icon;
        const value = stats[card.key];
        const isActive = activeFilter === card.filter;
        const t = TONES[card.tone];

        return (
          <button
            key={card.key}
            type="button"
            onClick={() => onStatClick?.(card.filter)}
            className={`group text-left rounded-2xl border p-4 transition-colors bg-card dark:bg-white/[0.06] dark:backdrop-blur-md orbit-box-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
              isActive
                ? t.activeRing
                : 'border-border/60 dark:border-white/[0.08] hover:border-foreground/30 dark:hover:border-white/[0.20]'
            }`}
            aria-pressed={isActive}
            aria-label={`${card.label}: ${value}`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 border transition-transform group-hover:scale-110 ${t.chip}`}>
                <Icon className={`w-4 h-4 ${t.icon}`} />
              </span>
              <span
                className={`font-display text-2xl font-semibold tabular-nums ${
                  isActive ? t.activeValue : 'text-foreground'
                }`}
              >
                {value}
              </span>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground leading-tight">
              {card.label}
            </p>
          </button>
        );
      })}
    </div>
  );
};
